import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  FaHeadset,
  FaShoppingCart,
  FaHeart,
  FaUser,
  FaBars,
  FaTimes,
  FaSearch
} from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import image from '../assets/sai-logo1.png';
import { navList } from '../Data/popularjson';
import {
  useLazyCartViewQuery,
  useLazySearchproductsQuery,
  useLazyViewWishlistQuery,
  useLogoutMutation
} from '../redux/Api/api';
import { saveCartCountHandler } from '../redux/reducer/CartCountSlice';
import { saveWishlistCountHandler } from '../redux/reducer/WhislistCountSlice';

const Navbar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // State management
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [name, setUsername] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [toggle, setToggle] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Redux selectors
  const selecttor = useSelector(state => state?.cartcount);
  const whishlistCount = useSelector(state => state?.wishlistCount);

  // API hooks
  const [userlogout] = useLogoutMutation();
  const [cartlist] = useLazyCartViewQuery();
  const [whishlist] = useLazyViewWishlistQuery();
  const [searchProducts] = useLazySearchproductsQuery();

  useEffect(() => {
    const currentPath = location.pathname;
    const activeItem = navList.find(item => item.navi === currentPath);
    if (activeItem) {
      setActiveMenu(activeItem.name);
    }
  }, [location.pathname]);

  const toggleMenu = (item) => {
    setIsMenuOpen(!isMenuOpen);
    setActiveMenu(item.name);
    navigate(item?.navi, { state: item?.mincat });
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const getcartlist = () => {
    const storedUser = JSON.parse(localStorage.getItem('user')) || null;
    const id = storedUser?._id;

    cartlist(id)
      .unwrap()
      .then(res => {
        dispatch(saveCartCountHandler(res?.length));
      })
      .catch(err => {
        console.log('Error:', err);
      });
  };

  const getWishlistData = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    const userId = user?._id;

    whishlist({ userId })
      .unwrap()
      .then(res => {
        if (res?.status === "success") {
          const data = res?.data;
          dispatch(saveWishlistCountHandler(data?.length));
        }
      })
      .catch(err => {
        console.error("Error fetching wishlist:", err);
      });
  };

  const performSearch = (value) => {
    setIsSearching(true);
    searchProducts(value)
      .unwrap()
      .then(res => {
        if (res?.result?.length > 0) {
          setSearchResults(res.result);
          setShowDropdown(true);
        } else {
          setSearchResults([]);
          setShowDropdown(false);
        }
      })
      .catch(err => {
        console.error('Search Error:', err);
        setSearchResults([]);
        setShowDropdown(false);
      })
      .finally(() => {
        setIsSearching(false);
      });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (!value.trim()) {
      setShowDropdown(false);
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      performSearch(value);
    }, 100);

    return () => clearTimeout(timeoutId);
  };

  const handleSearchSubmit = () => {
    if (!searchTerm.trim()) {
      toast.warning('Please enter a search term.');
      return;
    }
    performSearch(searchTerm);
  };

  const handleProductClick = (product) => {
    setShowDropdown(false);
    setSearchTerm('');
    navigate('/productview', { state: product })
  };

  const handleLogout = async () => {
    try {
      await userlogout()
        .unwrap()
        .then(() => {
          const storedUser = JSON.parse(localStorage.getItem('user'));
          if (storedUser?.id) {
            localStorage.removeItem(`cart_${storedUser.id}`);
          }
          localStorage.removeItem('user');
          setUsername('');
          setIsLoggedIn(false);
          setCartCount(0);
          setWishlistCount(0);
          navigate('/');
          toast.success('Logout successful!');
        });
    } catch (err) {
      console.error(err);
      toast.error('Failed to logout. Please try again.');
    }
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      getcartlist();
      getWishlistData();
      setUsername(storedUser.username);
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 w-full bg-white shadow z-50">
      <div className="bg-blue-500 text-white py-2">
        <div className="container mx-auto text-center">
          <p>Top-Quality Pets Food Here!</p>
        </div>
      </div>

      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center justify-between py-4">
          <div className="flex items-center w-full lg:w-auto justify-between">
            <Link to="/" className="flex items-center">
              <img src={image} className="w-[120px] lg:w-[200px]" alt="Logo" />
            </Link>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden text-gray-800 focus:outline-none"
            >
              {menuOpen ? (
                <FaTimes className="text-2xl" />
              ) : (
                
                <FaBars className="text-2xl" />
              )}
            </button>
          </div>

          <div className="w-full lg:w-1/2 mt-4 lg:mt-0">
            <div className="hidden lg:block relative w-full search-container mb-4 lg:mb-6">
              <div className="flex">
                <input
                  type="search"
                  placeholder="Search for products"
                  className="border-2 border-blue-500 p-3 w-full rounded-l-md"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <button
                  className="bg-blue-600 text-white px-6 py-3 rounded-r-md flex items-center hover:bg-blue-700 transition-colors"
                  onClick={handleSearchSubmit}
                  disabled={isSearching}
                >
                  {isSearching ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <FaSearch />
                  )}
                </button>
              </div>

              {showDropdown && searchResults.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 shadow-lg rounded-md max-h-[400px] overflow-y-auto z-50">
                  {searchResults.map((product) => (
                    <div
                      key={product._id}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      onClick={() => navigate('/productview', {state : product})}
                    >
                      <div className="flex items-center">
                        {/* <img
                          src={product.image}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-md mr-4"
                        /> */}
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800 mb-1">{product.name}</h3>
                          {/* <p className="text-sm text-gray-600">${product.price}</p> */}
                          {product.category && (
                            <p className="text-xs text-gray-500 mt-1">
                              Category: {product.category}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <nav className={`${menuOpen ? 'block' : 'hidden'} lg:block border-t lg:border-t-0 pt-4 lg:pt-0`}>
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between">
                <div className="flex flex-col lg:flex-row lg:space-x-8 w-full lg:w-auto">
                  {navList?.map((item) => (
                    <button
                      key={item.name}
                      className={`text-left py-2 lg:py-0 text-xl font-bold lg:text-1xl transition duration-300 ${
                        activeMenu === item.name 
                          ? 'text-blue-600 relative after:content-[""] after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-blue-600'
                          : 'text-gray-800 hover:text-blue-600'
                      }`}
                      onClick={() => toggleMenu(item)}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>

                <div className="hidden sm:flex items-center space-x-6 mt-4 lg:mt-0">
                  <Link to="/cart" className="relative">
                    <FaShoppingCart className="text-2xl text-blue-500 hover:text-blue-600 transition duration-300" />
                    {selecttor > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full px-1.5 text-xs">
                        {selecttor}
                      </span>
                    )}
                  </Link>
                  <Link to="/wishlist" className="relative">
                    <FaHeart className="text-2xl text-blue-500 hover:text-blue-600 transition duration-300" />
                    {whishlistCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full px-2 text-xs font-bold">
                        {whishlistCount}
                      </span>
                    )}
                  </Link>
                </div>
              </div>
            </nav>
          </div>

          <div className="hidden lg:flex items-center space-x-8 mt-4 mr-10 lg:mt-0">
            <div className="flex items-center space-x-3">
              <FaHeadset className="text-xl text-gray-600" />
              <div className="text-left text-gray-500">
                <span>Need Help?</span>
                <br />
                <a
                  href="tel:+91 63838 89291"
                  className="text-blue-600 hover:text-blue-800 font-semibold"
                >
                  +91 63838 89291
                </a>
              </div>
            </div>

            {isLoggedIn ? (
              <div className="relative group">
                <div className="cursor-pointer bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 transition duration-300">
                  <span className="truncate block max-w-[140px]">
                    Welcome, {name}
                  </span>
                </div>
                <div className="absolute right-0 mt-2 w-48 bg-white shadow-md rounded-md z-20 opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-300">
                  <ul className="text-blue-700">
                    <li>
                      <Link to="/personal-info" className="block px-4 py-2 hover:bg-gray-100">
                        My Account
                      </Link>
                    </li>
                    <li>
                      <Link to="/myorders" className="block px-4 py-2 hover:bg-gray-100">
                        Orders
                      </Link>
                    </li>
                    <li>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left block px-4 py-2 hover:bg-gray-100"
                      >
                        Logout
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            ) : (
              <Link
                to="/login"
                className="flex items-center bg-blue-500 text-white px-5 py-2 rounded hover:bg-blue-600 transition duration-300"
              >
                <FaUser className="mr-2" /> Login
              </Link>
            )}
          </div>

          <div className="lg:hidden w-1/2 mt-[-65px] ml-20 flex justify-center">
            {isLoggedIn ? (
              <div className="relative w-full">
                <button
                  onClick={toggleDropdown}
                  className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition duration-300 flex items-center justify-center"
                >
                  <span className="truncate">Welcome, {name}</span>
                </button>
                {isDropdownOpen && (
                  <div className="absolute left-0 right-0 mt-2 bg-white shadow-md rounded-md z-20">
                    <ul className="text-blue-700">
                      <li>
                        <Link to="/personal-info" className="block px-4 py-2 hover:bg-gray-100">
                          My Account
                        </Link>
                      </li>
                      <li>
                        <Link to="/myorders" className="block px-4 py-2 hover:bg-gray-100">
                          Orders
                        </Link>
                      </li>
                      <li>
                        <button
                          onClick={handleLogout}
                          className="w-full text-left block px-4 py-2 hover:bg-gray-100"
                        >
                          Logout
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="w-full bg-blue-500 text-white px-5 py-2 rounded hover:bg-blue-600 transition duration-300 flex items-center justify-center"
              >
                <FaUser className="mr-2" /> Login
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;